import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Banner } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";
import {
  FLEET_DEFAULT_DAYS,
  parseFleetActivationFilters,
  useFleetActivations,
  type FleetActivationSummary,
} from "@visioni/a3s-monitoring/app";
import { EMPTY } from "../format";
import { patriotToolsHref } from "../routes";
import { useDirectoryTenantName } from "../hooks/useDirectoryTenantName";
import { ActivationDateRangeField } from "../components/ActivationDateRangeField";
import { ActivationsTable } from "../components/ActivationsTable";
import { QueryNotice } from "../components/QueryNotice";

const rawId = (id: string) => id;

/**
 * Every activation in the replica, across tenants, filtered by date alone —
 * the fleet index knows nothing of sites or cameras.
 *
 * Rows link to the fleet detail page rather than a tenant's, which refuses
 * other tenants' tickets. Site and camera show as ids: the app only names the
 * selected tenant's.
 */
export function FleetActivationsScreen() {
  const router = useRouter();
  const { tenantId } = useLocalSearchParams<{ tenantId: string }>();
  const { from, to } = useLocalSearchParams<{ from?: string; to?: string }>();
  const parsed = parseFleetActivationFilters({ from, to }, new Date());

  const list = useFleetActivations({
    filters: parsed.ok ? parsed.filters : { since: "" },
    enabled: parsed.ok,
  });

  const tenantName = useDirectoryTenantName();
  const tenantLabel = (activation: FleetActivationSummary) =>
    activation.a3sTenant === undefined ? EMPTY : (tenantName(activation.a3sTenant) ?? activation.a3sTenant);

  return (
    <View style={styles.page}>
      <View style={styles.filterRow}>
        <View style={styles.field}>
          <ActivationDateRangeField
            // The defaulted start is shown rather than left blank, so the
            // picker says what the list is actually showing.
            value={parsed.ok ? parsed.days : { from, to }}
            onChange={(next) => router.setParams({ from: next.from, to: next.to })}
            placeholder={`Last ${String(FLEET_DEFAULT_DAYS)} days`}
          />
        </View>
      </View>
      {!parsed.ok ?
        <View style={styles.notice}>
          <Banner variant="warning" title="Filters not applied" message={parsed.problem} />
        </View>
      : list.items === undefined || list.error !== null ?
        <View style={styles.notice}>
          <QueryNotice isLoading={list.isLoading} error={list.error} onRetry={list.refetch} />
        </View>
      : list.items.length === 0 && list.nextCursor === null && !list.pager.canGoBack ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            No activations found
          </Text>
        </View>
      : <ActivationsTable
          items={list.items}
          isStale={list.isPlaceholderData}
          isFetching={list.isFetching}
          hrefFor={(activation) =>
            patriotToolsHref(tenantId, `fleet-activations/${encodeURIComponent(activation.ticketNo)}`)
          }
          tenantLabel={tenantLabel}
          siteLabel={rawId}
          cameraLabel={rawId}
          pager={list.pager}
          nextCursor={list.nextCursor}
        />
      }
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  page: { flex: 1 },
  filterRow: {
    flexDirection: "row",
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    // Above the list, so the date picker opens over the rows.
    zIndex: 1,
  },
  // One field of the tenant list's four-across row, so the picker is the same width.
  field: { flexBasis: "22%", minWidth: 160 },
  notice: { paddingHorizontal: theme.spacing.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", padding: theme.spacing["4xl"] },
}));
